import { FC, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    MenuItem,
    Stack,
    Switch,
    TextField,
} from '@mui/material';
import { ApiError } from '../../models/apiError';
import {
    CreateLibrarySectionRequest,
    LAYOUT_HINT_OPTIONS,
    LibrarySectionLayoutHint,
    LibrarySectionType,
    SectionTranslation,
} from '../../types/library';
import {
    useCreateLibrarySection,
    useDynamicRules,
} from '../../hooks/useLibrarySection';
import { LIBRARY_SECTION_ICON_OPTIONS } from '../../types/librarySectionIcons';

const TYPE_OPTIONS: Array<{ value: LibrarySectionType; label: string }> = [
    { value: 'CATEGORY', label: 'CATEGORY' },
    { value: 'CURATION', label: 'CURATION' },
    { value: 'DYNAMIC', label: 'DYNAMIC' },
];

const TRANSLATION_LANGS: Array<{ lang: string; label: string; required: boolean }> = [
    { lang: 'ko', label: '한국어', required: true },
    { lang: 'en', label: 'English', required: false },
    { lang: 'ja', label: '日本語', required: false },
    { lang: 'vi', label: 'Tiếng Việt', required: false },
];

const CODE_REGEX = /^[A-Z][A-Z0-9_]*$/;

interface Props {
    open: boolean;
    onClose: () => void;
    /** 현재 섹션 목록의 max sortOrder + 1 (기본 정렬 추천값) */
    nextSortOrder: number;
}

export const LibrarySectionAddDialog: FC<Props> = ({
    open,
    onClose,
    nextSortOrder,
}) => {
    const [type, setType] = useState<LibrarySectionType>('CATEGORY');
    const [code, setCode] = useState('');
    const [sortOrder, setSortOrder] = useState<number>(nextSortOrder);
    const [layoutHint, setLayoutHint] =
        useState<LibrarySectionLayoutHint>('CAROUSEL_1ROW');
    const [ruleCode, setRuleCode] = useState<string>('');
    const [icon, setIcon] = useState<string>('');
    const [isActive, setIsActive] = useState<boolean>(true);
    const [translations, setTranslations] = useState<Record<string, string>>({
        ko: '',
        en: '',
        ja: '',
        vi: '',
    });
    const [codeConflict, setCodeConflict] = useState<string | null>(null);

    const createSection = useCreateLibrarySection();
    const dynamicRulesQuery = useDynamicRules();
    const dynamicRules = useMemo(
        () => dynamicRulesQuery.data?.rules ?? [],
        [dynamicRulesQuery.data]
    );

    useEffect(() => {
        if (open) {
            setType('CATEGORY');
            setCode('');
            setSortOrder(nextSortOrder);
            setLayoutHint('CAROUSEL_1ROW');
            setRuleCode('');
            setIcon('');
            setIsActive(true);
            setTranslations({ ko: '', en: '', ja: '', vi: '' });
            setCodeConflict(null);
        }
    }, [open, nextSortOrder]);

    const codeValid = code.length === 0 || CODE_REGEX.test(code);
    const koValid = translations.ko.trim().length > 0;
    const ruleCodeRequired = type === 'DYNAMIC';
    const ruleCodeValid = !ruleCodeRequired || ruleCode.trim().length > 0;
    const formValid =
        code.length > 0 && codeValid && koValid && ruleCodeValid && !createSection.isPending;

    const handleSubmit = async () => {
        const trimmedCode = code.trim();
        if (!CODE_REGEX.test(trimmedCode)) {
            return;
        }

        const translationList: SectionTranslation[] = TRANSLATION_LANGS
            .map((l) => ({
                lang: l.lang,
                displayName: translations[l.lang]?.trim() ?? '',
            }))
            .filter((t) => t.displayName.length > 0);

        const req: CreateLibrarySectionRequest = {
            type,
            code: trimmedCode,
            sortOrder,
            layoutHint,
            isActive,
            translations: translationList,
        };
        if (ruleCodeRequired) {
            req.ruleCode = ruleCode;
        }
        const trimmedIcon = icon.trim();
        if (trimmedIcon.length > 0) {
            req.icon = trimmedIcon;
        }

        try {
            await createSection.mutateAsync(req);
            setCodeConflict(null);
            onClose();
        } catch (e) {
            if (e instanceof ApiError && (e.status === 409 || e.errorCode === 13)) {
                setCodeConflict(
                    e.message || '이미 사용 중인 code입니다. 다른 값으로 시도하세요.'
                );
            } else {
                const message = e instanceof Error ? e.message : '생성 실패';
                alert(`섹션 생성 실패: ${message}`);
            }
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>섹션 추가</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} mt={1}>
                    <TextField
                        select
                        label="type"
                        value={type}
                        onChange={(e) => {
                            const next = e.target.value as LibrarySectionType;
                            setType(next);
                            if (next !== 'DYNAMIC') {
                                setRuleCode('');
                            }
                        }}
                        fullWidth
                    >
                        {TYPE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        label="code"
                        value={code}
                        onChange={(e) => {
                            setCode(e.target.value.toUpperCase());
                            if (codeConflict) setCodeConflict(null);
                        }}
                        fullWidth
                        required
                        error={!codeValid || !!codeConflict}
                        helperText={
                            codeConflict
                                ? codeConflict
                                : !codeValid
                                  ? 'code는 [A-Z][A-Z0-9_]* 형식이어야 합니다 (예: BUSINESS_PICK)'
                                  : '생성 후 변경 불가. 대문자/숫자/언더스코어만 허용'
                        }
                    />

                    <TextField
                        label="sortOrder"
                        type="number"
                        value={sortOrder}
                        onChange={(e) => setSortOrder(Number(e.target.value))}
                        fullWidth
                        helperText="작을수록 먼저 노출"
                    />

                    <TextField
                        select
                        label="icon (chip 앞 아이콘, 선택)"
                        value={icon}
                        onChange={(e) => setIcon(e.target.value)}
                        fullWidth
                        helperText="모바일 chip 라벨 앞에 prefix로 노출되는 HugeIcons 코드"
                    >
                        <MenuItem value="">
                            <em>(없음)</em>
                        </MenuItem>
                        {LIBRARY_SECTION_ICON_OPTIONS.map((opt) => (
                            <MenuItem key={opt.code} value={opt.code}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        select
                        label="layoutHint"
                        value={layoutHint}
                        onChange={(e) =>
                            setLayoutHint(e.target.value as LibrarySectionLayoutHint)
                        }
                        fullWidth
                    >
                        {LAYOUT_HINT_OPTIONS.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                                {opt}
                            </MenuItem>
                        ))}
                    </TextField>

                    {type === 'DYNAMIC' && (
                        <TextField
                            select
                            label="ruleCode"
                            value={ruleCode}
                            onChange={(e) => setRuleCode(e.target.value)}
                            required
                            fullWidth
                            disabled={dynamicRulesQuery.isLoading}
                            helperText={
                                dynamicRulesQuery.isLoading
                                    ? '룰 목록 로딩 중...'
                                    : '서버 화이트리스트에서 선택'
                            }
                            error={ruleCodeRequired && !ruleCodeValid}
                        >
                            {dynamicRules.length === 0 ? (
                                <MenuItem value="" disabled>
                                    사용 가능한 룰이 없습니다
                                </MenuItem>
                            ) : (
                                dynamicRules.map((r) => (
                                    <MenuItem key={r.ruleCode} value={r.ruleCode}>
                                        {r.ruleCode}
                                        <Box
                                            component="span"
                                            sx={{
                                                ml: 1,
                                                color: 'text.secondary',
                                                fontSize: '0.75rem',
                                            }}
                                        >
                                            {r.description}
                                        </Box>
                                    </MenuItem>
                                ))
                            )}
                        </TextField>
                    )}

                    <FormControlLabel
                        control={
                            <Switch
                                checked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                            />
                        }
                        label="활성"
                    />

                    <Box>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            gap={2}
                        >
                            {TRANSLATION_LANGS.map((lang) => (
                                <TextField
                                    key={lang.lang}
                                    label={`${lang.label} (${lang.lang})${lang.required ? ' *' : ''}`}
                                    value={translations[lang.lang] ?? ''}
                                    onChange={(e) =>
                                        setTranslations((prev) => ({
                                            ...prev,
                                            [lang.lang]: e.target.value,
                                        }))
                                    }
                                    fullWidth
                                    required={lang.required}
                                    error={lang.required && !koValid}
                                    helperText={
                                        lang.required && !koValid
                                            ? '한국어 displayName 필수'
                                            : ''
                                    }
                                />
                            ))}
                        </Stack>
                    </Box>

                    {dynamicRulesQuery.isError && (
                        <Alert severity="warning">
                            ruleCode 화이트리스트를 불러오지 못했습니다.
                        </Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={createSection.isPending}>
                    취소
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!formValid}
                >
                    {createSection.isPending ? '생성 중...' : '생성'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
